import React from 'react'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix-fetcher'
import {
  ArrowPathIcon,
  MinusCircleIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline'
import { removeTicketsFromBasket, addTicketsToBasket } from '@lib/spektrix/index'
import { showBookingError } from '@lib/booking-error-handler'
import { syncMealDealsWithTickets } from '@lib/meal-deal-sync'

const UnreservedTickets = ({
  instance,
  plan,
  unreservedTicketsInBasket,
  ticketOptions,
  mutate,
  instanceData
}) => {
  const BASKET_DELAY = 1500
  const [updatingBasket, setUpdatingBasket] = React.useState(false)
  
  // Get basket data using SWR to ensure we have the latest
  const { data: currentBasket } = useSWR('/basket', SpektrixFetcher)

  const TicketOption = ({ option }) => {
    let inBasket = unreservedTicketsInBasket.filter(
      t => t.type === option.value
    )

    return (
      <div className='flex items-center justify-between y-3'>
        <button
          onClick={async () => {
            setUpdatingBasket(true)
            const toRemove = inBasket[0]
            if (!toRemove) {
              setUpdatingBasket(false)
              return
            }
            
            try {
              // Use the common sync function
              await syncMealDealsWithTickets({
                action: 'remove',
                ticketsToProcess: [toRemove.id],
                instanceData: instanceData,
                mealDealInfo: instance,
                currentBasket: currentBasket
              })
              
              if (mutate) mutate()
              setTimeout(() => {
                setUpdatingBasket(false)
              }, BASKET_DELAY)
            } catch (error) {
              setUpdatingBasket(false)
              alert(`Error removing ticket: ${error.message || 'Unknown error'}`)
            }
          }}
          disabled={updatingBasket || inBasket.length === 0}
          className='disabled:opacity-50 group'
        >
          <MinusCircleIcon className='w-8 h-8 text-blue-600 dark:text-blue-400 group-enabled:hover:bg-blue-600 dark:group-enabled:hover:bg-blue-500 rounded-full group-enabled:hover:text-white' />
        </button>
        <div className='flex items-center space-x-1 text-gray-900 dark:text-white'>
          {updatingBasket ? (
            <ArrowPathIcon className='w-4 h-4 animate-spin mr-2' />
          ) : (
            <span>{inBasket.length > 0 ? `${inBasket.length} x` : ''}</span>
          )}
          <span>{option.label}</span>
        </div>
        <button
          onClick={async () => {
            setUpdatingBasket(true)
            // Create ticket data in the format expected by Spektrix API
            const ticketData = [{
              instance: instance.spektrix_v3_id || instance.id,
              seatingPlan: plan.spektrix_v3_id,
              type: option.value
            }]
            
            try {
              // Use the common sync function
              await syncMealDealsWithTickets({
                action: 'add',
                ticketsToProcess: ticketData,
                instanceData: instanceData,
                mealDealInfo: instance,
                currentBasket: currentBasket
              })
              
              if (mutate) mutate()
              setTimeout(() => {
                setUpdatingBasket(false)
              }, BASKET_DELAY)
            } catch (error) {
              setUpdatingBasket(false)
              // Use global showBookingError if available (for priority booking error handling)
              if (window.showBookingError) {
                window.showBookingError(error)
              } else {
                showBookingError(error)
              }
            }
          }}
          disabled={updatingBasket}
          className='disabled:opacity-50 group'
        >
          <PlusCircleIcon className='w-8 h-8 text-blue-600 dark:text-blue-400 group-enabled:hover:bg-blue-600 dark:group-enabled:hover:bg-blue-500 rounded-full group-enabled:hover:text-white' />
        </button>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-3'>
      {ticketOptions?.map(option => (
        <TicketOption option={option} key={option.value} />
      ))}
    </div>
  )
}

export default UnreservedTickets
